import React, { useState, useEffect, useMemo, useRef } from 'react';
import { withStyles, Typography, Button, Tooltip, IconButton, Dialog } from '@material-ui/core';
import { connect } from 'react-redux';
import PCProductList from './PCProductList';
import { hasPermission, createHistory, parseURL, formatDate } from '../../sc-utilities';
import { History, FindInPage, Print } from '@material-ui/icons';
import * as actions from './store/pcProductActions';
import { NavLink, Redirect } from 'react-router-dom';
import {
    getStorageInfo,
    getGraphicComponent,
    getErasureStatuses,
    getVerificationStatuses,
    getMemoryInfo,
    getProcessorInfo,
    getNICComponent,
} from '../product.utilities';
import * as userActions from '../../np-core/User/store/userActions';
import PrintLabelForm from '../PrintLabelForm';
import QRCode from 'qrcode.react';
import '../qrCode.scss';
import ActiveWarehouseForm from '../../sc-logistic/Colli/ActiveWarehouseForm';
import * as colliActions from '../../sc-logistic/Colli/store/colliActions';
import * as groupActions from '../../sc-group/Group/store/groupActions';
import AppLogoLoading from '../../sc-logo';
import { getGroupsLookup } from '../../sc-dashboard/ProductOverview/ProductsAll';
import { checkIfLoading } from '../../sc-users/User';

const styles = (theme) => ({
    '@global': {
        "[class*='paletteButtons']": {
            backgroundColor: theme.palette.primary.main,
            color: 'none',
            margin: theme.spacing(),
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
            },
            "[class*='MuiButton-contained']": {
                backgroundColor: theme.palette.primary.main,
                color: 'none',
                margin: theme.spacing(),
                '&:hover': {
                    backgroundColor: theme.palette.secondary.main,
                },
            },
            "[class*='MuiTypography-root.chip_NotErased.MuiTypography-body1']": {
                color: theme.palette.warning.dark,
                padding: 1,
                fontSize: 12,
            },
            "[class*='MuiTypography-root.chip_Started.MuiTypography-body1']": {
                color: theme.palette.primary.main,
                padding: 1,
                fontSize: 12,
            },
            "[class*='MuiTypography-root.chip_Erased.MuiTypography-body1']": {
                color: theme.palette.success.dark,
                padding: 1,
                fontSize: 12,
            },
            "[class*='MuiTypography-root.chip_InProgress.MuiTypography-body1']": {
                color: theme.palette.info.dark,
                padding: 1,
                fontSize: 12,
            },
            "[class*='MuiTypography-root.chip_Error.MuiTypography-body1']": {
                color: theme.palette.error.dark,
                padding: 1,
                fontSize: 12,
            },
            "[class*='MuiTypography-root.chip_Warning.MuiTypography-body1']": {
                color: theme.palette.warning.light,
                padding: 1,
                fontSize: 12,
            },
            "[class*='MuiButtonBase-root']": {
                textTransform: 'none',
                textDecoration: 'none',
                color: 'none',
            },
        },
    },
    paletteButtons: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.buttonText.default,
        margin: theme.spacing(),
        minWidth: '24px',
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
    },
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%',
        },
    },
    loading: {
        backgroundColor: 'transparent',
        boxShadow: 'none',
        overflow: 'hidden',
        alignItems: 'center',
    },
});

const PCProduct = (props) => {
    const {
        classes,
        translations,
        permissions,
        activeCustomerID,
        columns,
        onFetchWarehouse,
        setActiveWarehouse,
        activeWarehouseID,
        activeCustomer,
        fetchGroups,
    } = props;
    let searchParams = parseURL(window.location.search);

    let visible = columns && columns['PCProduct'];
    const [redirectOptions, setRedirectOptions] = useState({
        redirect: false,
        orderID: null,
        redirectPath: '',
    });
    const [printLabelState, setPrintLabelState] = useState({
        open: false,
        productID: null,
        printers: [],
    });
    const [warehouseActive, setWarehouseActive] = useState({
        loading: false,
        open: false,
        data: [],
        name: [],
    });

    const tableRef = useRef();
    const [isPrinted, setIsPrinted] = useState(false);
    const [groups, setGroups] = useState(null);
    const [loading, setLoading] = useState({ groups: false });

    const getIcon = (icon) => {
        switch (icon) {
            case 'FindInPage':
                return <FindInPage />;
            case 'History':
                return <History />;
            case 'Print':
                return <Print />;
            default:
                return <></>;
        }
    };

    const fetchData = async (id) => {
        await props.fetchUserSettings({ ActiveCustomerID: activeCustomerID });
        let data = await props.onFetchPrinters({
            ActiveCustomerID: activeCustomerID,
        });
        setPrintLabelState({ open: true, productID: id, printers: data });
    };
    const handlePrintLabel = async (id) => {
        if (!id) {
            setPrintLabelState({ open: false, productID: null, printers: [] });
            return;
        }
        let result = await props.onProductPrinted({
            ActiveCustomerID: activeCustomerID,
            ProductID: id,
        });
        setIsPrinted(result);
        fetchData(id);
    };

    useEffect(() => {
        const getGroups = async () => {
            setLoading((prevState) => {
                return { ...prevState, groups: true };
            });
            const resultGroups = await fetchGroups({ ActiveCustomerID: activeCustomerID });
            if (resultGroups) {
                setGroups(resultGroups.data);
            }
            setLoading((prevState) => {
                return { ...prevState, groups: false };
            });
        };

        if (hasPermission(permissions, 'group_list')) {
            getGroups();
        }
        // eslint-disable-next-line
    }, []);

    const groupsLookup = getGroupsLookup(groups, translations, permissions);

    useEffect(() => {
        const fetchWarehouse = async () => {
            let warehouses = await onFetchWarehouse({ ActiveCustomerID: activeCustomerID });
            let data;
            if (!activeWarehouseID && !(searchParams && searchParams.WarehouseInventoryID)) {
                if (warehouses.length > 1) {
                    setWarehouseActive({ loading: true, open: true, data: warehouses, name: warehouses });
                }
                if (warehouses.length === 1) {
                    await setActiveWarehouse({
                        ActiveCustomerID: activeCustomerID,
                        ActiveWarehouseID: warehouses[0].value,
                    });
                    data = warehouses.filter((data) => data.value === activeWarehouseID);
                    setWarehouseActive({ loading: true, open: false, data: warehouses, name: warehouses });
                }
                if (warehouses.length === 0) {
                    setWarehouseActive({ loading: true, open: false, data: [], name: [] });
                }
            }
            if (activeWarehouseID && !(searchParams && searchParams.WarehouseInventoryID)) {
                if (warehouses.length > 0) {
                    data = warehouses.filter((params) => params.value === activeWarehouseID);
                    setWarehouseActive({ loading: true, open: false, data: warehouses, name: data });
                }
                if (warehouses.length === 0) {
                    setWarehouseActive({ loading: true, open: false, data: [], name: [] });
                }
                if (warehouses.length === 1) {
                    await setActiveWarehouse({
                        ActiveCustomerID: activeCustomerID,
                        ActiveWarehouseID: warehouses[0].value,
                    });
                    setWarehouseActive({ loading: true, open: false, data: warehouses, name: warehouses });
                }
            }
            if (searchParams && searchParams.WarehouseInventoryID) {
                if (warehouses.length > 1) {
                    data = warehouses.filter(
                        (params) => params.inventoryID.toString() === searchParams.WarehouseInventoryID
                    );
                    setWarehouseActive({ loading: true, open: false, data: warehouses, name: data });
                }
                if (warehouses.length === 0) {
                    setWarehouseActive({ loading: true, open: false, data: [], name: [] });
                }
                if (warehouses.length === 1) {
                    await setActiveWarehouse({
                        ActiveCustomerID: activeCustomerID,
                        ActiveWarehouseID: warehouses[0].value,
                    });
                    setWarehouseActive({ loading: true, open: false, data: warehouses, name: warehouses });
                }
            }
        };
        fetchWarehouse();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeWarehouseID]);

    const tableActionsRow = [
        {
            icon: 'FindInPage',
            tooltip: translations['product_details'] || 'Product  details',
            onClick: (event, rowData) => productComponentHandler(rowData.ProductID),
            hidden: !hasPermission(permissions, 'product_load'),
        },
        {
            icon: 'History',
            tooltip: translations['product_history_view'] || 'View product histories',
            onClick: (event, rowData) => productHistoriesHandler(rowData.ProductID),
            hidden: !hasPermission(permissions, 'product_history'),
        },
        {
            icon: 'Print',
            tooltip: translations['print_label'] || 'Print label',
            onClick: (event, rowData) => handlePrintLabel(rowData.ProductID),
            hidden: !hasPermission(permissions, 'product_print_label'),
        },
    ];

    let tableColumns = useMemo(() => {
        return [
            {
                title: translations['action'] || 'Action',
                field: 'Action',
                hidden: visible ? visible['Action'] : false,
                cellStyle: { minWidth: 200, maxWidth: 200 },
                filtering: false,
                sorting: false,
                render: (rowData) => {
                    return tableActionsRow.map((action) => {
                        if (action.hidden) {
                            return null;
                        }
                        const iconButton = (
                            <IconButton component="div" onClick={(event) => action.onClick(event, rowData)}>
                                {getIcon(action.icon)}
                            </IconButton>
                        );

                        const activeCustomerParams = `&ActiveCustomerID=${activeCustomerID}&ActiveCustomerName=${activeCustomer?.Name}`;

                        const detailsLink = `/pc-product/details?productID=${rowData.ProductID}&type=PCProduct${activeCustomerParams}`;
                        const historiesLink = `/product/histories?productID=${rowData.ProductID}`;

                        return (
                            <Tooltip key={`${action.icon}-${rowData.OrderID}`} title={action.tooltip}>
                                {action.icon === 'FindInPage' || action.icon === 'History' ? (
                                    <NavLink to={action.icon === 'FindInPage' ? detailsLink : historiesLink}>
                                        {iconButton}
                                    </NavLink>
                                ) : (
                                    iconButton
                                )}
                            </Tooltip>
                        );
                    });
                },
            },
            {
                field: 'Status',
                title: translations['status'] || 'Status',
                hidden: visible ? visible['Status'] : false,
                filtering: false,
                sorting: false,
                cellStyle: {
                    width: 120,
                    minWidth: 120,
                },
                headerStyle: {
                    width: 120,
                    minWidth: 120,
                },
                lookup: {
                    erased: translations['erased'] || 'Erased',
                    noterased: translations['not_erased'] || 'Not Erased',
                    inprogress: translations['in_progress'] || 'In progress',
                    error: translations['error'] || 'Error',
                    warning: translations['warning'] || 'Warning',
                    aborted: translations['aborted_by_user'] || 'Aborted by user',
                    started: translations['started'] || 'Started',
                },
                defaultFilter: ['erased', 'noterased', 'inprogress', 'error', 'warning', 'aborted', 'started'],
                render: (rowData) => {
                    return getErasureStatuses(rowData, null, classes, translations);
                },
            },
            {
                field: 'QRCode',
                title: translations['qr_code'] || 'QR Code',
                hidden: visible ? visible['QRCode'] : true,
                filtering: false,
                sorting: false,
                cellStyle: {
                    width: 120,
                    minWidth: 120,
                },
                headerStyle: {
                    width: 120,
                    minWidth: 120,
                },
                render: (rowData) => {
                    return getQRCode(rowData.InventoryID);
                },
            },
            {
                field: 'OrderInventoryID',
                title: translations['order'] || 'Order',
                hidden: visible ? visible['OrderInventoryID'] : false,
                defaultFilter: searchParams && searchParams.OrderInventoryID ? searchParams.OrderInventoryID : '',
                sorting: false,
                render: (rowData) => {
                    return rowData.Order ? (
                        <Button
                            color="primary"
                            variant="contained"
                            className={classes.paletteButtons}
                            href={`/logistic/orders?OrderInventoryID=${rowData.Order.OrderInventoryID}`}
                        >
                            <Typography variant={'caption'}>{rowData.Order.OrderInventoryID}</Typography>
                        </Button>
                    ) : (
                        <Typography variant={'caption'}> n/a </Typography>
                    );
                },
            },
            {
                field: 'TransportColliInventoryID',
                title: translations['transport_collies'] || 'Transport collies',
                hidden: visible ? visible['TransportColliInventoryID'] : false,
                defaultFilter:
                    searchParams && searchParams.TransportColliInventoryID
                        ? searchParams.TransportColliInventoryID
                        : '',
                sorting: false,
                render: (rowData) => {
                    return rowData.TransportColli ? (
                        <Button
                            color="primary"
                            variant="contained"
                            className={classes.paletteButtons}
                            href={`/logistic/transport-container?TransportColliInventoryID=${rowData.TransportColli.TransportColliInventoryID}`}
                        >
                            <Typography variant={'caption'}>
                                {rowData.TransportColli.TransportColliInventoryID}
                            </Typography>
                        </Button>
                    ) : (
                        <Typography variant={'caption'}> n/a </Typography>
                    );
                },
            },
            {
                field: 'ColliInventoryID',
                title: translations['colli'] || 'Colli',
                defaultFilter: searchParams && searchParams.ColliInventoryID ? searchParams.ColliInventoryID : '',
                sorting: false,
                hidden: visible ? visible['ColliInventoryID'] : false,
                render: (rowData) => {
                    return rowData.Colli ? (
                        <Button
                            color="primary"
                            variant="contained"
                            className={classes.paletteButtons}
                            href={`/logistic/container?ColliInventoryID=${rowData.Colli.ColliInventoryID}`}
                        >
                            <Typography variant={'caption'}>{rowData.Colli.ColliInventoryID}</Typography>
                        </Button>
                    ) : (
                        <Typography variant={'caption'}> n/a </Typography>
                    );
                },
            },
            {
                field: 'InventoryID',
                title: translations['inventory_id'] || 'Securaze ID',
                sorting: false,
                hidden: visible ? visible['InventoryID'] : false,
                defaultFilter: searchParams && searchParams.InventoryID ? searchParams.InventoryID : '',
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.InventoryID ? rowData.InventoryID : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                field: 'InventoryNumber',
                title: translations['inventory_number'] || 'Inventory number',
                sorting: false,
                hidden: visible ? visible['InventoryNumber'] : false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.InventoryNumber
                                ? rowData.InventoryNumber
                                : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                field: 'SKU',
                title: translations['sku'] || 'SKU',
                hidden: visible ? visible['SKU'] : false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData?.SKU ?? (translations['short_not_assigned'] || 'n/a')}
                        </Typography>
                    );
                },
            },
            {
                title: translations['group'] || 'Group',
                field: 'GroupID',
                sorting: false,
                cellStyle: { minWidth: 100, maxWidth: 100 },
                hidden: visible ? visible['GroupID'] : false,
                lookup: groupsLookup,
                defaultFilter: searchParams?.GroupID ? [searchParams.GroupID] : groups ? Object.keys(groupsLookup) : [],
                render: (rowData) => {
                    return (
                        <Typography style={{ fontSize: 12 }} gutterBottom>
                            {rowData?.Group ? rowData.Group?.Name : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                field: 'ChassisType',
                sorting: false,
                title: translations['chassis'] || 'Chassis',
                hidden: visible ? visible['ChassisType'] : false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.PCProduct && rowData.PCProduct.Chassis
                                ? rowData.PCProduct.Chassis
                                : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                field: 'Vendor',
                sorting: false,
                title: translations['vendor'] || 'Vendor',
                hidden: visible ? visible['Vendor'] : false,
                render: (rowData) => {
                    return (
                        <>
                            {' '}
                            {rowData.Vendor && rowData.Vendor.length > 14 ? (
                                <Tooltip key={`${rowData.Vendor}-${rowData.ProductID}`} title={rowData.Vendor}>
                                    <Typography variant={'caption'}>{rowData.Vendor.slice(0, 13)}</Typography>
                                </Tooltip>
                            ) : (
                                <Typography variant={'caption'}>
                                    {rowData.Vendor ? rowData.Vendor : translations['short_not_assigned'] || 'n/a'}
                                </Typography>
                            )}
                        </>
                    );
                },
            },
            {
                field: 'Model',
                sorting: false,
                title: translations['model'] || 'Model',
                hidden: visible ? visible['Model'] : false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.PCProduct && rowData.PCProduct.Name
                                ? rowData.PCProduct.Name
                                : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['serial'] || 'Serial',
                field: 'SerialNumber',
                sorting: false,
                hidden: visible ? visible['SerialNumber'] : false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.SerialNumber ? rowData.SerialNumber : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
                cellStyle: {
                    width: 120,
                    minWidth: 120,
                },
                headerStyle: {
                    width: 120,
                    minWidth: 120,
                },
            },
            {
                title: translations['manual_entered_serial_number'] || 'Manual entered serial number',
                field: 'ManualEnteredSerialNumber',
                sorting: false,
                hidden: visible ? visible['ManualEnteredSerialNumber'] : false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.ManualEnteredSerialNumber
                                ? rowData.ManualEnteredSerialNumber
                                : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['anumber'] || 'ANumber',
                field: 'ANumber',
                sorting: false,
                hidden: visible ? visible['ANumber'] : false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData?.PCProduct?.ANumber ?? (translations['short_not_assigned'] || 'n/a')}
                        </Typography>
                    );
                },
            },
            {
                title: translations['grade'] || 'Grade',
                field: 'Grade',
                hidden: visible ? visible['Grade'] : false,
                filtering: false,
                sorting: false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.Grade && rowData.Grade.InternationalGrading
                                ? rowData.Grade.InternationalGrading
                                : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['created_at'] || 'Created at',
                field: 'CreatedAt',
                hidden: visible ? visible['CreatedAt'] : false,
                filtering: false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.CreatedAt
                                ? formatDate(rowData.CreatedAt)
                                : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['processor_name'] || 'Processor name',
                field: 'ProcessorName',
                hidden: visible ? visible['ProcessorName'] : false,
                filtering: false,
                sorting: false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {getProcessorInfo(rowData, 'name', classes, translations)}
                        </Typography>
                    );
                },
            },
            {
                title: translations['processor_speed'] || 'Processor speed',
                field: 'ProcessorSpeed',
                filtering: false,
                sorting: false,
                hidden: visible ? visible['ProcessorSpeed'] : false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {getProcessorInfo(rowData, 'speed', classes, translations)}
                        </Typography>
                    );
                },
            },
            {
                title: translations['total_memory'] || 'Total memory',
                field: 'TotalMemory',
                filtering: false,
                sorting: false,
                hidden: visible ? visible['TotalMemory'] : false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {getMemoryInfo(rowData, 'total', classes, translations)}
                        </Typography>
                    );
                },
            },
            {
                title: translations['memory_vendor'] || 'Memory vendor',
                field: 'MemoryVendor',
                filtering: false,
                sorting: false,
                hidden: visible ? visible['MemoryVendor'] : false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {getMemoryInfo(rowData, 'vendor', classes, translations)}
                        </Typography>
                    );
                },
            },
            {
                title: translations['memory_components'] || 'Memory components',
                field: 'MemoryComponents',
                hidden: visible ? visible['MemoryComponents'] : false,
                filtering: false,
                sorting: false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {getMemoryInfo(rowData, 'components', classes, translations)}
                        </Typography>
                    );
                },
            },
            {
                title: translations['memory_size'] || 'Memory size',
                field: 'MemorySize',
                hidden: visible ? visible['MemorySize'] : false,
                filtering: false,
                sorting: false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {getMemoryInfo(rowData, 'size', classes, translations)}
                        </Typography>
                    );
                },
            },
            {
                title: translations['nic_product'] || 'NIC product',
                field: 'NICProduct',
                filtering: false,
                sorting: false,
                hidden: visible ? visible['NICProduct'] : false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>{getNICComponent(rowData.Components, translations)}</Typography>
                    );
                },
            },
            {
                title: translations['graphic_card'] || 'Graphic card',
                field: 'GraphicCard',
                hidden: visible ? visible['GraphicCard'] : false,
                filtering: false,
                sorting: false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {getGraphicComponent(rowData.Components, translations)}
                        </Typography>
                    );
                },
            },
            {
                title: translations['disk_vendor'] || 'Disk vendor',
                field: 'DiskVendor',
                hidden: visible ? visible['DiskVendor'] : false,
                filtering: false,
                sorting: false,
                render: (rowData) => {
                    return <Typography variant={'caption'}>{getStorageInfo(rowData, 'vendor', classes)}</Typography>;
                },
            },
            {
                title: translations['disk_model'] || 'Disk model',
                field: 'DiskModel',
                hidden: visible ? visible['DiskModel'] : false,
                filtering: false,
                sorting: false,
                render: (rowData) => {
                    return <Typography variant={'caption'}>{getStorageInfo(rowData, 'model', classes)}</Typography>;
                },
                cellStyle: {
                    width: 120,
                    minWidth: 120,
                },
                headerStyle: {
                    width: 120,
                    minWidth: 120,
                },
            },
            {
                title: translations['disk_serial'] || 'Disk serial',
                field: 'DiskSerial',
                hidden: visible ? visible['DiskSerial'] : false,
                filtering: false,
                sorting: false,
                render: (rowData) => {
                    return <Typography variant={'caption'}>{getStorageInfo(rowData, 'serial', classes)}</Typography>;
                },
            },
            {
                title: translations['wipe_status'] || 'Wipe status',
                field: 'WipeStatus',
                hidden: visible ? visible['WipeStatus'] : false,
                filtering: false,
                sorting: false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {getErasureStatuses(rowData, 'status', classes, translations)}
                        </Typography>
                    );
                },
                lookup: {
                    erased: translations['erased'] || 'Erased',
                    noterased: translations['not_erased'] || 'Not Erased',
                    inprogress: translations['in_progress'] || 'In progress',
                    error: translations['error'] || 'Error',
                    warning: translations['warning'] || 'Warning',
                    aborted: translations['aborted_by_user'] || 'Aborted by user',
                },
                defaultFilter: ['erased', 'noterased', 'inprogress', 'error', 'warning', 'aborted'],
                cellStyle: {
                    width: 120,
                    minWidth: 120,
                },
                headerStyle: {
                    width: 120,
                    minWidth: 120,
                },
            },
            {
                title: translations['verification_status'] || 'Verification status',
                field: 'VerificationStatus',
                hidden: visible ? visible['VerificationStatus'] : false,
                filtering: false,
                sorting: false,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {getVerificationStatuses(rowData, true, classes, translations)}
                        </Typography>
                    );
                },
                lookup: {
                    verified: translations['verified'] || 'Verified',
                    notverified: translations['not_verified'] || 'Not Verified',
                    inprogress: translations['in_progress'] || 'In progress',
                    error: translations['error'] || 'Error',
                    warning: translations['warning'] || 'Warning',
                    aborted: translations['aborted_by_user'] || 'Aborted by user',
                },
                defaultFilter: ['verified', 'notverified', 'inprogress', 'error', 'warning', 'aborted'],
                cellStyle: {
                    width: 120,
                    minWidth: 120,
                },
                headerStyle: {
                    width: 120,
                    minWidth: 120,
                },
            },
            {
                title: translations['disk_size'] || 'Disk size',
                field: 'DiskSize',
                hidden: visible ? visible['DiskSize'] : false,
                filtering: false,
                sorting: false,
                render: (rowData) => {
                    return <Typography variant={'caption'}>{getStorageInfo(rowData, 'size', classes)}</Typography>;
                },
                cellStyle: {
                    width: 120,
                    minWidth: 120,
                },
                headerStyle: {
                    width: 120,
                    minWidth: 120,
                },
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes, translations, visible, groupsLookup]);

    tableColumns = hasPermission(permissions, 'group_list')
        ? tableColumns
        : tableColumns.filter((column) => column.field !== 'GroupID');

    const getQRCode = (inventoryID) => {
        return (
            <div className="tooltip">
                <QRCode size={50} value={`<Product>${inventoryID}</Product>`} />
                <span>
                    <QRCode size={200} value={`<Product>${inventoryID}</Product>`} />
                </span>
            </div>
        );
    };

    const productComponentHandler = async (product) => {
        createHistory();
        setRedirectOptions({
            redirect: true,
            productID: product,
            redirectPath: `/pc-product/details?productID=${product}&type=PCProduct`,
        });
    };

    const productHistoriesHandler = (productID) => {
        createHistory();
        setRedirectOptions({
            redirect: true,
            productID: productID,
            redirectPath: `/product/histories?productID=${productID}`,
        });
    };

    const goToCreatePCProduct = () => {
        createHistory();
        setRedirectOptions({
            redirect: true,
            PCProductID: null,
            redirectPath: `/order/create`,
        });
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    const loadingScreen = (
        <Dialog
            disableBackdropClick={true}
            disableEscapeKeyDown={false}
            open={checkIfLoading(loading)}
            PaperProps={{ classes: { root: classes.loading } }}
        >
            <AppLogoLoading />
        </Dialog>
    );

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'product_list') && warehouseActive.loading ? (
                !warehouseActive.open ? (
                    loading.groups ? (
                        loadingScreen
                    ) : (
                        <PCProductList
                            groups={groups}
                            warehouseActive={warehouseActive}
                            setWarehouseActive={setWarehouseActive}
                            tableColumns={tableColumns}
                            goToCreatePCProduct={goToCreatePCProduct}
                            tableRef={tableRef}
                        />
                    )
                ) : (
                    <ActiveWarehouseForm
                        setWarehouseActive={setWarehouseActive}
                        handleCloseActiveWarehouseForm={() => setWarehouseActive({ ...warehouseActive, open: false })}
                        warehouses={warehouseActive}
                    />
                )
            ) : null}
            <PrintLabelForm
                translations={translations}
                isPrinted={isPrinted}
                textButton={translations['print'] || 'PRINT'}
                activeCustomer={activeCustomerID}
                type={'pcProduct'}
                printLabel={props.type === 'PCProduct' ? props.onPrintLabelPC : props.onPrintLabel}
                userSettings={props.userSettings}
                productID={printLabelState.productID}
                open={printLabelState.open}
                printers={printLabelState.printers}
                handleOpen={handlePrintLabel}
            />
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        activeCustomer: state.core.login.activeCustomer,
        activeWarehouseID: state.core.login.user.ActiveWarehouseID,
        pcProducts: state.product.pcProducts,
        translations:
            state.translate && state.translate.translations
                ? state.translate.translations.activeTranslations || {}
                : {},
        userSettings: state?.core?.login?.userSetting,
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || [],
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        onProductPrinted: (pcProduct) => dispatch(actions.isPrintedLabel(pcProduct)),
        onFetchPrinters: (id) => dispatch(actions.fetchPrinters(id)),
        setActiveWarehouse: (params) => dispatch(colliActions.setActiveWarehouse(params)),
        onFetchWarehouse: (params) => dispatch(colliActions.listWarehouse(params)),
        onPrintLabel: (data) => dispatch(actions.printLabel(data)),
        fetchUserSettings: (id) => dispatch(userActions.fetchUserSettings(id)),
        fetchGroups: (params) => dispatch(groupActions.fetchGroups(params)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles, { withTheme: true })(PCProduct));
